import { useAnalytics } from '@ecosystem/newron-client-lib';
import { containers } from '@ecosystem/newron-design-system';
import React from 'react';
import { useSelector } from 'react-redux';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { SalesPerformance } from '@models/SalesPerformance';
import { NoResult } from '@pages/NoResult';
import { PERFORMANCE_CARD_TITLE } from '@pages/Utilities';
import { Kpi } from '@utils/Formatters';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, PageName, PageType } from '@utils/pageConfig/Types';
import MarketPerformanceSummary from '../market-performance-summary/MarketPerformanceSummary';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import * as styles from './MarketPerformancePosFacts.module.scss';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { constructCurrencyLabel } from '@utils/currencyUtils/CurrencyUtils';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import ErrorResultMessage from '@pages/ErrorResultMessage';

export interface PosFactsProps {
  revenuePerformance?: SalesPerformance;
  volumePerformance?: SalesPerformance;
  selectedGrowthType: GrowthTypes;
  isLoading: boolean;
  factRevenue: FactRevenue;
  volume: string;
  pageName: PageName;
  isError?: boolean;
}

const MarketPerformancePosFacts: React.FC<PosFactsProps> = ({
  revenuePerformance,
  volumePerformance,
  selectedGrowthType,
  isLoading,
  factRevenue,
  volume,
  pageName,
  isError = false,
}) => {
  const pagesConfig = usePageConfig();
  //Start Page Element View Amplitude Tracking
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersStateRO = useSelector((state: any) => state.mainFiltersRO);
  const mainFilterStateMarketPremium = useSelector((state: any) => state.mainFiltersMarketPremium);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, pageName);

  const createParam: () => PageElementViewTrackingParam = () => {
    return {
      eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: pagesConfig.pageType,
      pageName: pageName,
      componentName: PERFORMANCE_CARD_TITLE,
      elementType: ElementType.TILES,
      mainFilterState:
        pagesConfig.pageType === PageType.CATEGORY_LEADERBOARD ? mainFiltersStateRO : mainFilterStateMarketPremium,
      hasData: !!revenuePerformance && !!volumePerformance,
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, pagesConfig.pageType, effectiveProductType),
    };
  };
  const { ref: CategoryLeaderboardPerformanceComponent } = useInViewTracking(createParam);
  const currencyLabel = constructCurrencyLabel(factRevenue);
  //End Page Element View Amplitude Tracking
  const { Card } = containers;
  return renderVisualization({
    isLoading,
    fetchedApiData: volumePerformance,
    defaultData: new SalesPerformance(),
    noResultPredicate: () => !volumePerformance || !revenuePerformance,
    jsxToRender: (
      <div className={styles.PosFacts} ref={CategoryLeaderboardPerformanceComponent}>
        <div className={styles.PosFacts__RevenuePerformance}>
          <div className={styles.PosFacts__MarketPerformanceReport}>
            <span>Revenue{currencyLabel} Performance</span>
            <div data-testid="revenue-performance">
              <Card.Content>
                <MarketPerformanceSummary
                  performance={revenuePerformance}
                  selectedGrowthType={selectedGrowthType}
                  kpiType={Kpi.marketRevenue}
                  factRevenue={factRevenue}
                />
              </Card.Content>
            </div>
          </div>
        </div>
        <div className={styles.PosFacts__VolumePerformance}>
          <span>{volume} Performance</span>
          <div data-testid="volume-performance">
            <Card.Content>
              <MarketPerformanceSummary
                performance={volumePerformance}
                selectedGrowthType={selectedGrowthType}
                kpiType={Kpi.yourVolume}
                factRevenue={factRevenue}
              />
            </Card.Content>
          </div>
        </div>
      </div>
    ),
    noResultJsx: <div ref={CategoryLeaderboardPerformanceComponent}>{NoResult()}</div>,
    errorMessageJsx: <div ref={CategoryLeaderboardPerformanceComponent}>{ErrorResultMessage()}</div>,
    isError: isError,
  });
};

export default MarketPerformancePosFacts;
